import { useEffect, useRef, useState, useCallback } from 'react';
import { getSocket } from '../services/socket';
import api from '../services/api';

/**
 * useWebRTC — handles incoming WebRTC offers from the phone for an alert.
 * Returns refs to attach to <video> elements and per-stream status.
 *
 * Phase 3: video-only, two streams (front + back camera).
 */
export function useWebRTC(alertId) {
  const frontVideoRef = useRef(null);
  const backVideoRef = useRef(null);
  const peersRef = useRef({});       // { front_camera: RTCPeerConnection, back_camera: ... }
  const streamsRef = useRef({});     // cache of MediaStreams to re-attach if refs change
  const localMicStreamRef = useRef(null);  // cached MediaStream for operator mic (two-way voice)
  const [iceServers, setIceServers] = useState(null);
  const [micEnabled, setMicEnabled] = useState(false);
  const [streamStatus, setStreamStatus] = useState({
    front_camera: 'waiting',
    back_camera: 'waiting',
  });

  // Lazily capture the operator's microphone AND webcam. Called on first SOS connect.
  // Browsers require a prior user gesture (the dispatch click qualifies).
  // Combining audio+video in one getUserMedia() keeps both tracks in a single
  // MediaStream so they share a sync clock on the phone side.
  const ensureMic = useCallback(async () => {
    if (localMicStreamRef.current) return localMicStreamRef.current;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
        video: {
          width: { ideal: 320 },
          height: { ideal: 240 },
          frameRate: { ideal: 15, max: 20 },
          facingMode: 'user',
        },
      });
      localMicStreamRef.current = stream;
      console.log('[useWebRTC] 🎤📹 operator mic + webcam acquired');
      return stream;
    } catch (err) {
      console.warn('[useWebRTC] mic/cam permission denied or unavailable:', err.message);
      // Fallback: try audio-only if video fails
      try {
        const audioOnly = await navigator.mediaDevices.getUserMedia({
          audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
          video: false,
        });
        localMicStreamRef.current = audioOnly;
        console.log('[useWebRTC] 🎤 operator mic only (no webcam)');
        return audioOnly;
      } catch (e) {
        console.warn('[useWebRTC] audio-only fallback failed too:', e.message);
        return null;
      }
    }
  }, []);

  // Toggle the operator's outgoing audio track on/off without tearing down the peer
  const toggleMic = useCallback(() => {
    const stream = localMicStreamRef.current;
    if (!stream) return;
    stream.getAudioTracks().forEach(t => { t.enabled = !t.enabled; });
    setMicEnabled(stream.getAudioTracks().some(t => t.enabled));
  }, []);

  // Re-attach stream to video element (used both on track event and ref change)
  const attachStream = useCallback((streamType, stream) => {
    streamsRef.current[streamType] = stream;
    const videoEl = streamType === 'front_camera'
      ? frontVideoRef.current
      : backVideoRef.current;
    if (videoEl && stream) {
      if (videoEl.srcObject !== stream) {
        videoEl.srcObject = stream;
      }
      // Try to play, retry on failure (autoplay policies)
      const tryPlay = () => {
        videoEl.play().catch(err => {
          console.warn(`[useWebRTC] play() blocked for ${streamType}, retrying:`, err.message);
          setTimeout(tryPlay, 500);
        });
      };
      tryPlay();
    }
  }, []);

  // Re-attach when refs become available (e.g. modal opens) - only when status changes
  useEffect(() => {
    Object.keys(streamsRef.current).forEach(streamType => {
      attachStream(streamType, streamsRef.current[streamType]);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [streamStatus.front_camera, streamStatus.back_camera]);

  // Fetch ICE servers once
  useEffect(() => {
    api.get('/webrtc/ice-servers')
      .then(res => {
        console.log('[useWebRTC] ICE servers:', res.data.iceServers);
        setIceServers(res.data.iceServers);
      })
      .catch(err => console.error('[useWebRTC] ICE fetch failed:', err));
  }, []);

  // Setup signaling listeners when alert + ICE ready
  useEffect(() => {
    if (!alertId || !iceServers) return;
    const socket = getSocket();
    if (!socket) {
      console.warn('[useWebRTC] Socket not connected');
      return;
    }

    socket.emit('alert:join', alertId);

    const handleOffer = async (data) => {
      const { alertId: incomingAlertId, streamType, offer, fromSocketId } = data;
      if (incomingAlertId !== alertId) return;

      console.log(`[useWebRTC] 📥 offer for ${streamType}`);

      // If a peer already exists for this stream, close it before creating new one
      // (handles the phone retrying offers every 5s)
      if (peersRef.current[streamType]) {
        try { peersRef.current[streamType].close(); } catch (e) {}
      }

      setStreamStatus(s => ({ ...s, [streamType]: 'connecting' }));

      const pc = new RTCPeerConnection({
        iceServers,
        bundlePolicy: 'max-bundle',
        rtcpMuxPolicy: 'require',
      });
      peersRef.current[streamType] = pc;

      pc.ontrack = (event) => {
        console.log(`[useWebRTC] 🎥 track for ${streamType}, streams:`, event.streams.length);
        const stream = event.streams[0] || new MediaStream([event.track]);
        attachStream(streamType, stream);
      };

      pc.onicecandidate = (event) => {
        if (event.candidate) {
          socket.emit('webrtc:ice-candidate', {
            alertId,
            streamType,
            toSocketId: fromSocketId,
            candidate: {
              candidate: event.candidate.candidate,
              sdpMid: event.candidate.sdpMid,
              sdpMLineIndex: event.candidate.sdpMLineIndex,
            },
          });
        }
      };

      pc.onconnectionstatechange = () => {
        console.log(`[useWebRTC] ${streamType} state: ${pc.connectionState}`);
        if (pc.connectionState === 'connected') {
          setStreamStatus(s => ({ ...s, [streamType]: 'live' }));
        } else if (['failed', 'closed'].includes(pc.connectionState)) {
          setStreamStatus(s => ({ ...s, [streamType]: 'failed' }));
        }
      };

      pc.oniceconnectionstatechange = () => {
        console.log(`[useWebRTC] ${streamType} ICE: ${pc.iceConnectionState}`);
      };

      try {
        await pc.setRemoteDescription(new RTCSessionDescription(offer));

        // Two-way media: attach operator mic + webcam to the front_camera peer only.
        // We only need one back-channel — adding these to both peers would
        // create duplicate audio/video for the phone user.
        if (streamType === 'front_camera') {
          const micStream = await ensureMic();
          if (micStream) {
            micStream.getTracks().forEach(track => {
              try {
                pc.addTrack(track, micStream);
                console.log(`[useWebRTC] 📤 operator ${track.kind} track attached to front peer`);
              } catch (e) {
                console.warn(`[useWebRTC] addTrack(${track.kind}) failed:`, e.message);
              }
            });
            setMicEnabled(micStream.getAudioTracks().some(t => t.enabled));
          }
        }

        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);

        socket.emit('webrtc:answer', {
          alertId,
          streamType,
          toSocketId: fromSocketId,
          answer: { type: answer.type, sdp: answer.sdp },
        });
        console.log(`[useWebRTC] 📤 answer sent for ${streamType}`);
      } catch (err) {
        console.error(`[useWebRTC] negotiation failed for ${streamType}:`, err);
        setStreamStatus(s => ({ ...s, [streamType]: 'failed' }));
      }
    };

    const handleIceCandidate = (data) => {
      const { streamType, candidate } = data;
      const pc = peersRef.current[streamType];
      if (pc && candidate && pc.remoteDescription) {
        pc.addIceCandidate(new RTCIceCandidate(candidate))
          .catch(e => console.warn(`[useWebRTC] ICE add failed for ${streamType}:`, e.message));
      }
    };

    socket.on('webrtc:offer', handleOffer);
    socket.on('webrtc:ice-candidate', handleIceCandidate);

    return () => {
      socket.off('webrtc:offer', handleOffer);
      socket.off('webrtc:ice-candidate', handleIceCandidate);
      Object.values(peersRef.current).forEach(pc => {
        try { pc.close(); } catch (e) {}
      });
      peersRef.current = {};
      streamsRef.current = {};
      // Release the mic so the browser indicator goes away between alerts
      if (localMicStreamRef.current) {
        try {
          localMicStreamRef.current.getTracks().forEach(t => t.stop());
        } catch (e) {}
        localMicStreamRef.current = null;
        setMicEnabled(false);
      }
    };
  }, [alertId, iceServers, attachStream, ensureMic]);

  return { frontVideoRef, backVideoRef, streamStatus, streamsRef, micEnabled, toggleMic };
}
